import { resolveElementalFx, type ElementalFxId, type StageMaterial } from './elyonElementalFx'
import type { AttackKind } from './simulation'
import type { ImpactCue, ImpactCueKind } from './store'
import type { StageBreakState } from './stageDestruction'

export type ImpactFragmentShape='spark'|'shard'|'chip'|'droplet'|'leaf'
export type ImpactVisualProfile={
 primary:string
 secondary:string
 fragmentColor:string
 fragmentShape:ImpactFragmentShape
 fragmentCount:number
 fragmentSize:number
 fragmentSpin:number
 baseScale:number
 growth:number
 ringOpacity:number
 coreOpacity:number
 spin:number
 rotationZ:number
 height:number
 lightIntensity:number
 lightDistance:number
 directionalLength:number
 floorOpacity:number
 floorRadius:number
 breakRays:number
 duration:number
}

const KIND_COLOR:Record<ImpactCueKind,string>={hit:'#fff2cf',throw:'#ffbf6b',block:'#72dfff',parry:'#c98cff','guard-break':'#ff7b4f'}
const MATERIAL_STYLE:Record<StageMaterial,{color:string;shape:ImpactFragmentShape;size:number}>={
 stone:{color:'#d4bca0',shape:'chip',size:.075},
 crystal:{color:'#b783ff',shape:'shard',size:.085},
 wood:{color:'#b9784f',shape:'chip',size:.082},
 metal:{color:'#8bb8d8',shape:'spark',size:.068},
 organic:{color:'#6be27c',shape:'leaf',size:.078},
 earth:{color:'#a78b62',shape:'chip',size:.088},
 water:{color:'#5bc8ff',shape:'droplet',size:.072},
}
const KIND_LIFE:Record<ImpactCueKind,number>={hit:.18,throw:.24,block:.14,parry:.26,'guard-break':.3}

function attackWeight(kind:AttackKind|null){return kind==='special'?1.32:kind==='heavy'?1.16:kind==='throw'?1.08:1}
function breakWeight(state:StageBreakState|undefined){return state==='collapsed'?1.62:state==='broken'?1.38:state==='cracked'?1.16:1}
function breakRays(state:StageBreakState|undefined,material:StageMaterial|undefined){return state==='collapsed'?8:state==='broken'?6:state==='cracked'?4:material?2:0}
function particleShape(id:ElementalFxId):ImpactFragmentShape{
 const particle=resolveElementalFx(id).particle
 if(particle==='shard')return'shard'
 if(particle==='droplet')return'droplet'
 if(particle==='leaf')return'leaf'
 if(particle==='spark')return'spark'
 return'chip'
}

export function resolveImpactVisualProfile(cue:ImpactCue):ImpactVisualProfile{
 const elemental=resolveElementalFx(cue.elementalFxId)
 const material=cue.stageMaterial?MATERIAL_STYLE[cue.stageMaterial]:undefined
 const attack=attackWeight(cue.attackKind),breaking=breakWeight(cue.stageBreakState)
 const defensive=cue.kind==='block'||cue.kind==='parry'||cue.kind==='guard-break'
 const primary=defensive?KIND_COLOR[cue.kind]:elemental.accent
 const secondary=material?.color??elemental.primary[1]
 const baseFragments=cue.kind==='guard-break'?8:cue.kind==='parry'?6:cue.kind==='throw'?5:cue.kind==='block'?3:4
 const fragmentCount=Math.max(2,Math.min(12,Math.round(baseFragments*attack*breaking)))
 const baseScale=cue.kind==='guard-break'?1:cue.kind==='parry'?.9:cue.kind==='throw'?.82:.72
 const growth=cue.kind==='guard-break'?1.25:cue.kind==='parry'?.85:cue.kind==='throw'?.92:cue.kind==='block'?.52:.7
 const ringOpacity=cue.kind==='parry'?.92:cue.kind==='guard-break'?.88:cue.kind==='throw'?.82:cue.kind==='block'?.68:.72
 const coreOpacity=cue.kind==='hit'?.92:cue.kind==='throw'?.78:cue.kind==='guard-break'?.64:.55
 const spin=cue.kind==='parry'?8:cue.kind==='guard-break'?5.2:cue.kind==='throw'?4:cue.kind==='block'?2.8:2
 const rotationZ=cue.kind==='parry'?.45:cue.kind==='block'?.16:cue.kind==='throw'?.28:cue.kind==='guard-break'?.08:0
 const height=cue.kind==='guard-break'?1.35:cue.kind==='throw'?.92:1.15
 const lightIntensity=(cue.kind==='guard-break'?7.4:cue.kind==='parry'?5.2:cue.kind==='throw'?4.2:cue.kind==='block'?3.1:2.7)*attack*breaking
 const lightDistance=(cue.kind==='guard-break'?5.4:cue.kind==='throw'?4.2:3.4)*Math.min(1.35,attack*breaking)
 const floorOpacity=cue.stageMaterial?Math.min(.48,.12*breaking+(cue.stageBreakState?.08:0)):0
 const floorRadius=(cue.kind==='guard-break'?1.15:cue.kind==='throw'?.9:.72)*attack*breaking
 return{
  primary,secondary,fragmentColor:secondary,
  fragmentShape:material?.shape??particleShape(cue.elementalFxId),
  fragmentCount,fragmentSize:(material?.size??.072)*Math.min(1.45,attack*breaking),
  fragmentSpin:spin*(cue.source==='player'?1:-1),
  baseScale,growth:growth*attack*breaking,ringOpacity,coreOpacity,spin,rotationZ,height,
  lightIntensity,lightDistance,
  directionalLength:(cue.kind==='hit'?.9:cue.kind==='guard-break'?.65:cue.kind==='throw'?.5:0)*attack,
  floorOpacity,floorRadius,breakRays:breakRays(cue.stageBreakState,cue.stageMaterial),
  duration:KIND_LIFE[cue.kind],
 }
}
